import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import IconButton from '@savvy-kit/components/icon-button';
import {
  ArrowDown,
  ArrowUp,
  CircleDollarSign,
  MoreVertical,
  PencilLine,
  RefreshCw,
  Trash2,
  Wallet,
} from 'lucide-react';
import { useConfirmationDialog } from 'src/hooks/use-confirmation-dialog';

import type { AccountRow } from './types';
import {
  ACCOUNTS_HIDE_FROM_NET_WORTH_LABEL,
  ACCOUNTS_INCLUDE_IN_NET_WORTH_LABEL,
  ACCOUNTS_MOVE_DOWN_LABEL,
  ACCOUNTS_MOVE_UP_LABEL,
  ACCOUNTS_REFRESH_LABEL,
  ACCOUNTS_REMOVE_CONFIRM_LABEL,
  ACCOUNTS_REMOVE_LABEL,
  ACCOUNTS_RENAME_LABEL,
  ACCOUNTS_ROW_MENU_LABEL,
  ACCOUNTS_UPDATE_BALANCE_LABEL,
} from './constants';
import { canUpdateAccountBalance, removeAccountDescription, removeAccountTitle } from './utils';

type AccountsRowMenuProps = {
  readonly row: AccountRow;
  readonly position: { readonly isFirst: boolean; readonly isLast: boolean };
  readonly canReorder: boolean;
  readonly canManage: boolean;
  readonly canRename: boolean;
  readonly onRefresh: () => void;
  readonly onRename: (row: AccountRow) => void;
  readonly onUpdateBalance: (row: AccountRow) => void;
  readonly onRemove: (row: AccountRow) => Promise<void>;
  readonly onToggleNetWorth: (row: AccountRow) => Promise<void>;
  readonly onNudge: (offset: number) => void;
};

export function AccountsRowMenu({
  row,
  position,
  canReorder,
  canManage,
  canRename,
  onRefresh,
  onRename,
  onUpdateBalance,
  onRemove,
  onToggleNetWorth,
  onNudge,
}: AccountsRowMenuProps) {
  const showConfirmation = useConfirmationDialog();
  const canToggleNetWorth = canManage || !row.countsTowardNetWorth;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <IconButton variant="ghost" size="sm" aria-label={`${row.name} — ${ACCOUNTS_ROW_MENU_LABEL}`}>
            <MoreVertical size={16} aria-hidden="true" />
          </IconButton>
        }
      />
      <DropdownMenuContent align="end" className="min-w-44">
        {canReorder && (
          <DropdownMenuItem
            disabled={position.isFirst}
            onClick={() => {
              onNudge(-1);
            }}
          >
            <ArrowUp size={14} aria-hidden="true" />
            {ACCOUNTS_MOVE_UP_LABEL}
          </DropdownMenuItem>
        )}
        {canReorder && (
          <DropdownMenuItem
            disabled={position.isLast}
            onClick={() => {
              onNudge(1);
            }}
          >
            <ArrowDown size={14} aria-hidden="true" />
            {ACCOUNTS_MOVE_DOWN_LABEL}
          </DropdownMenuItem>
        )}
        {canUpdateAccountBalance(row) && (
          <DropdownMenuItem
            onClick={() => {
              onUpdateBalance(row);
            }}
          >
            <CircleDollarSign size={14} aria-hidden="true" />
            {ACCOUNTS_UPDATE_BALANCE_LABEL}
          </DropdownMenuItem>
        )}
        {canRename && (
          <DropdownMenuItem
            onClick={() => {
              onRename(row);
            }}
          >
            <PencilLine size={14} aria-hidden="true" />
            {ACCOUNTS_RENAME_LABEL}
          </DropdownMenuItem>
        )}
        {canManage && row.providerLinked && (
          <DropdownMenuItem onClick={onRefresh}>
            <RefreshCw size={14} aria-hidden="true" />
            {ACCOUNTS_REFRESH_LABEL}
          </DropdownMenuItem>
        )}
        {canToggleNetWorth && (
          <DropdownMenuItem
            onClick={() => {
              void onToggleNetWorth(row).catch(() => undefined);
            }}
          >
            <Wallet size={14} aria-hidden="true" />
            {row.countsTowardNetWorth ? ACCOUNTS_HIDE_FROM_NET_WORTH_LABEL : ACCOUNTS_INCLUDE_IN_NET_WORTH_LABEL}
          </DropdownMenuItem>
        )}
        {canManage && (
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              showConfirmation({
                title: removeAccountTitle(row),
                description: removeAccountDescription(row),
                confirmationLabel: ACCOUNTS_REMOVE_CONFIRM_LABEL,
                confirmationVariant: 'destructive',
                onConfirmation: () => onRemove(row),
              });
            }}
          >
            <Trash2 size={14} aria-hidden="true" />
            {ACCOUNTS_REMOVE_LABEL}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
